import React, { useCallback, useEffect, useRef, useState } from 'react';
import { IcUserCheck, IcClose, IcKeyRound, IcCheck, IcCheckCircle, IcLoader } from '../theme/icons';
import sellerIdentityService from '../services/sellerIdentityService';

// « Qui encaisse ? » — s'ouvre au clic sur « Encaisser la vente ». Le vendeur
// tape son code AU CLAVIER puis Entrée (code simple exprès : 1 à 12 lettres ou
// chiffres, « 1 », « k »…, sans différence maj/min).
// onIdentified({ id, username, method }) ; onCancel() = vente non faite.
const PIN_MAX = 12;
const cleanCode = (v) => v.replace(/[^a-zA-Z0-9]/g, '').slice(0, PIN_MAX);

// title / reason : ce qui est demandé (« Qui encaisse ? », « Mode manager »…).
// minLevel ('manager' | 'owner') = action RÉSERVÉE : le code d'un manager.
const SellerIdentifyModal = ({ title, reason, minLevel, onIdentified, onCancel }) => {
  const [pin, setPin] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [welcome, setWelcome] = useState(null);
  const doneRef = useRef(false);
  const pinInputRef = useRef(null);
  // The sale page re-renders constantly: keep its callbacks in a ref so the
  // effects below never restart because a prop changed identity.
  const cbRef = useRef({ onIdentified, onCancel });
  cbRef.current = { onIdentified, onCancel };
  const cancel = useCallback(() => { if (!doneRef.current) cbRef.current.onCancel(); }, []);

  const succeed = useCallback((seller) => {
    if (doneRef.current) return;
    doneRef.current = true;
    setError('');
    setWelcome(seller);
    // Le nom reste affiché un instant : on voit QUI a été reconnu avant que
    // la vente parte.
    setTimeout(() => cbRef.current.onIdentified(seller), 650);
  }, []);

  // ---- Code tapé au clavier ----
  const submitPin = async () => {
    if (busy || doneRef.current || !pin) return;
    setBusy(true);
    setError('');
    try {
      succeed(await sellerIdentityService.identifyByPin(pin, minLevel));
    } catch (err) {
      setPin('');
      setError(err.detail || 'Code inconnu.');
      // le champ revient prêt pour un nouvel essai
      setTimeout(() => { if (pinInputRef.current) pinInputRef.current.focus(); }, 0);
    } finally {
      setBusy(false);
    }
  };

  // Le champ du code prend le clavier dès l'ouverture.
  useEffect(() => {
    if (pinInputRef.current) pinInputRef.current.focus();
  }, []);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); cancel(); }
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [cancel]);

  return (
    <div className="modal-backdrop z-[70] flex items-center justify-center p-4" onMouseDown={(e) => { if (e.target === e.currentTarget) cancel(); }}>
      <div role="dialog" aria-modal="true" aria-labelledby="seller-identify-title" className="bg-white rounded-2xl shadow-2xl w-full max-w-sm overflow-hidden">
        <div className="modal-head">
          <h3 id="seller-identify-title" className="modal-title text-lg flex items-center gap-2"><IcUserCheck /> {title || (minLevel ? 'Un manager' : 'Qui êtes-vous ?')}</h3>
          <button type="button" onClick={cancel} className="text-gray-400 hover:text-white" aria-label="Fermer"><IcClose /></button>
        </div>

        <div className="p-5">
          {welcome ? (
            <div className="py-8 flex flex-col items-center gap-2 text-center" role="status">
              <IcCheckCircle className="text-green-600" size={40} />
              <p className="text-lg font-semibold text-gray-900">{welcome.name || welcome.username}</p>
              <p className="text-sm text-gray-500">{minLevel ? 'Accord donné' : 'Reconnu'} — un instant…</p>
            </div>
          ) : (
            <div>
              {reason && <p className="text-sm text-gray-600 mb-4">{reason}</p>}
              <label htmlFor="seller-code" className="block text-sm font-medium text-gray-900 mb-1.5">Votre code</label>
              <div className="relative">
                <IcKeyRound className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400" />
                {/* Champ texte masqué par le style (pas type=password : le
                    navigateur proposerait d'enregistrer un « mot de passe »). */}
                <input
                  id="seller-code"
                  ref={pinInputRef}
                  type="text"
                  value={pin}
                  onChange={(e) => { setPin(cleanCode(e.target.value)); setError(''); }}
                  onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); submitPin(); } }}
                  disabled={busy}
                  maxLength={PIN_MAX}
                  autoComplete="off"
                  autoCapitalize="off"
                  autoCorrect="off"
                  spellCheck={false}
                  aria-invalid={!!error}
                  aria-describedby="seller-code-help"
                  className={`inp ${error ? 'inp-err' : ''} w-full pl-11 pr-3 py-2.5 rounded-xl text-base text-gray-900`}
                  style={{ WebkitTextSecurity: 'disc' }}
                />
              </div>
              <p id="seller-code-help" role="alert" className="mt-1.5 min-h-[20px] text-sm">
                {error ? <span className="text-red-600">{error}</span> : <span className="text-gray-500">Tapez-le au clavier, puis Entrée.</span>}
              </p>
              <button type="button" onClick={submitPin} disabled={busy || !pin} className="btn-p mt-2 w-full py-2.5 rounded-xl text-sm gap-2">
                {busy ? <><IcLoader className="animate-spin" /> Vérification…</> : <><IcCheck /> Valider</>}
              </button>
            </div>
          )}
        </div>

        {!welcome && (
          <div className="px-5 py-3 border-t border-gray-100 flex justify-end">
            <button type="button" onClick={cancel} className="btn-g px-5 py-2.5 text-sm">Annuler</button>
          </div>
        )}
      </div>
    </div>
  );
};

export default SellerIdentifyModal;
